import * as React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import partial from 'lodash/partial';
import safeGet from 'lodash/get';
import { errorForm, interaction } from '@origin-digital/reporting-client';
import getUserLocation from './geotargetting/geotargetting';
import { AlertBox, Spinner } from '@origin-digital/style-guide';
import { markUpUtils } from '@origin-digital/utils-toolbox';
import './compare-plans.scss';
import * as planActions from '../services/plans-service';
import { getContent } from '../services/content-service';
import PlansCarousel from './plans-carousel/plans-carousel';
import PlansContainer from './plans-container/plans-container';
import FAQ from './faq/faq';
import SessionPersistence from '../services/session-persistence';
import SettingsFilters from './settings-filters/settings-filters';
import StateMarshaller from '../services/state-marshaller';
import {
  getMessage,
  getPlanEligibilityError,
  getServerError,
  setMessages,
} from '../services/messages';
import { isFuelEligibleForPurchase } from './settings-filters/utils';
import TermsAndConditions from './terms-and-conditions/terms-and-conditions';
import CallUsBusiness from './callus-business/call-us-business';
import env from '../services/env';
import * as cpReducers from '../reducers';
import { MediaQueryProvider } from 'react-media-query-hoc';
import { CONFIG } from '../common/mediaQuery';
import {
  areFilterValuesBasicallyTheSame,
  excludePlans,
  getAddressIDQqueryParam,
  getLPGCrossSellLink,
  getTrackingPlansNames,
  injectRecommendedPlan,
  personalisePlans,
  shouldGeoTarget,
  sortPlans,
  doSolarShuffle,
  doVicCulling
} from './compare-plans-utils';
import { FULL, NONE } from '../constants/fuel-availability-codes';
import { RESI_PLAN_ORDER, SME_PLAN_ORDER } from '../constants/plansOrder.metadata';
import STATIC_COPY from '../constants/copy';
import NOTIFICATIONS from '../constants/notifications';
import { getBPIDLinks, IComparisonPlan } from '@origin-digital/plans';
import { ConnectionAddressObject } from '@origin-digital/address-lookup';
import { getWindow } from '../window/window';
import { ORIGIN_SALES_DETAIL, SELECTED_PLAN } from '../constants/session-storage-names';
import { HelpMeChoose } from './help-me-choose/HelpMeChoose';
import { FeatureProviderContext } from '@origin-digital/features';
import { isFeatureEnabled } from '../utils/feature-toggle';
import { PersonalisedHeader } from './oe-631';
import { IMainState } from './compare-plans.types';

const MAX_PLANS_TO_SHOW = 3;

const sessionPersistence = SessionPersistence({
  persistenceTarget: env.contextHubInstance,
  storeName: 'origin',
});

const stateManager = StateMarshaller(sessionPersistence);

const initialState: IMainState = {
  user: stateManager.getUser(),
  /**
   * Filters refers to the state of the form at the top of the application
   */
  filters: stateManager.getFilters(),
  /**
   * This is the list of plan objects that the user is eligible for
   */
  plans: [],
  /**
   * Store the current address, if any
   */
  qasAddressObject: undefined,
  plansQueryEvent: {
    status: 'ready',
  },
  addressUpdate: false,
  /**
   * Initial fuel availbility is unknown as we have no postcode yet,
   * so we make both types avaible
   */
  fuelEligibilityStatus: {
    electricity: FULL,
    gas: FULL,
  },
  plansToExclude: [],
  error: undefined,
  showTargetTestTncs: false,
  termsAndConditions: {
    defaultTerms: 'vic',
    act: '',
    nsw: '',
    qld: '',
    nt: '',
    sa: '',
    tas: '',
    vic: '',
    wa: '',
  },
};

const removeNotifications = (sessionPersistence) => {
  const {
    HIDE_HEADERS,
    HEADER_MESSAGE,
    FUEL_MESSAGES,
    FUEL_SUBMISSION_TITLE,
    FUEL_ERROR_NOTIFICATIONS,
  } = NOTIFICATIONS;
  [
    FUEL_MESSAGES,
    FUEL_SUBMISSION_TITLE,
    HIDE_HEADERS,
    HEADER_MESSAGE,
    FUEL_ERROR_NOTIFICATIONS,
  ].forEach((key) => {
    sessionPersistence.removeItem(key);
  });
};

const needsGeoTargeting = shouldGeoTarget(initialState, sessionPersistence);

export interface IRootProps {
  plansApiEndPoint: string;
  factSheetApiEndPoint: string;
  dapiApiKey: string;
  contentApiEndPoint: string;
  messages: string[];
  getStartedUrlResi: string;
  getStartedUrlBusi: string;
  isGasOrDualEnabled: boolean;
  isGreenOptionEnabled: boolean;
  isSolarEnabled: boolean;
  enableAER: boolean;
  addressApiProps: {
    url: string;
    apiKey: string;
    qasAuthToken: string;
    reverseLookupUrl: string;
  };
  featureContext: FeatureProviderContext;
  personalisedPlanData?: any;
}

export default class Root extends React.Component<IRootProps, IMainState> {
  static defaultProps = {
    messages: {},
  };

  constructor(props: IRootProps) {
    super(props);
    this.state = cloneDeep(initialState);
  }

  async componentDidMount() {
    removeNotifications(sessionPersistence);
    const searchByPostcode = this.state.user.userAddressId === '';
    const keepGoing = (state) => {
      try {
        stateManager.setFilters(state.filters);
        stateManager.setUser(state.user);
      } catch {
        // catch exception when trying to set region to null - this happens when
        // user has no prior cookies/contexthub data set OR on first incognito load
        // keep calm and fetch plans
      } finally {
        this.getPlans(searchByPostcode);
      }
    };

    /**
     * If the user is visiting the CP page for the very first time, since
     * we're loading the Adobe Target analytics utilities script async, we
     * can't be sure that by the time this app is ready to render, that we
     * have the `target_state` cookie set. It's normally set by the Target
     * script, but if it's not available, we need to do an explicit
     * geo-targeting lookup to figure out where the user is so we can pre-
     * fill their filters to where we think they are.
     *
     * Unfortunately, since this is asynchronous, we can't do it in the
     * `constructor` method for this class -- everything there must be
     * synchronous -- so we do it here instead. Yes, that does mean that the
     * first render in `componentDidMount` could be immediately flushed away
     * when we call `setState` down below, but this only happens for the
     * small subset of users for whom we need to do this. For everyone else,
     * the app has the data ready by this point and can continue as normall,
     * not requiring a `setState` call.
     */
    if (needsGeoTargeting) {
      try {
        const userLocation = await getUserLocation();

        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState(
          (state) => cpReducers.updateUserRegionAndPostcode(userLocation, state),
          () => keepGoing(this.state),
        );
      } catch (e) {
        console.error('Could not geo-target the user');
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState(
          (state) => cpReducers.updateUserRegionAndPostcode(null, state),
          () => keepGoing(this.state),
        );
      }
    } else {
      keepGoing(this.state);
    }

    // retrive content from AEM
    this.getContent();
  }

  getContent = async () => {
    const { user } = this.state;
    getContent(
      user.isResidential === true ? 'residential' : 'business',
      this.props.contentApiEndPoint,
      this.props.dapiApiKey,
    )
      .then((response) => {
        setMessages(response.messages);
        this.setState({ termsAndConditions: response.termsAndConditions });
        return Promise.resolve('');
      })
      .catch((error) => {
        console.log('Error while retrieving content', error);
        return Promise.reject();
      });
  };

  getPlansOrder = () => {
    const { user } = this.state;
    return user.isResidential ? RESI_PLAN_ORDER : SME_PLAN_ORDER;
  };

  /**
   * Get the plans data from the plans API. If the addressId has been set in contextHub we need to make a call to the
   * address lookup service first in order to convert the AddressId to a usable format (i.e. 123 Happy Street). Once
   * we have this new format we make a call to the Plans API with the address parameters (i.e unit, street, city, etc)
   * If we dont have the addressId in contextHub we make a call to the Plans API with the default post code.
   * @param searchByPostcode
   */
  getPlans = async (searchByPostcode: boolean) => {
    const stateUpdate = {
      plansQueryEvent: {
        status: 'executing',
      },
      addressUpdate: false,
      error: null,
    };

    if (searchByPostcode) {
      stateUpdate.qasAddressObject = undefined;
    }
    this.setState(stateUpdate);

    const { user, filters } = this.state;
    let params = { ...filters, ...user };

    if (searchByPostcode) {
      this.getPlansApi({
        ...params,
        searchByPostcode,
      });
    } else {
      this.getPlansApi({
        ...params,
        addressId: user.userAddressId,
        searchByPostcode: false,
      });
      this.setState(
        partial(
          cpReducers.setPostcodeFilter,
          safeGet(this.state, 'qasAddressObject.qasAddress.postcode', ''),
        ),
      );
    }
  };

  /**
   * Util function to handle the promise from the Plans API call.
   * When we receive the response we need to inject a object
   * into each plan containing details for the charges section.
   * @param params - The parameters needed for the API call
   */
  getPlansApi = (params) => {
    params.useCache = isFeatureEnabled(this.props.featureContext, 'sales-core-cache-cp');

    // Cancel all previous requests
    planActions.getCancelToken()();
    planActions
      .getPlans(this.props.plansApiEndPoint || env.apiUrls.plans, params)
      .then((response) => {
        const isElectricityEligibleForPurchase = isFuelEligibleForPurchase(
          response.fuelEligibilityStatus.electricity,
        );
        const isGasEligibleForPurchase = isFuelEligibleForPurchase(
          response.fuelEligibilityStatus.gas,
        );
        const requiresElectricity =
          this.state.filters.servicesRequired.electricity && isElectricityEligibleForPurchase;
        const requiresGas = this.state.filters.servicesRequired.gas && isGasEligibleForPurchase;

        stateManager.setUser({
          userAddressId: response.fuelEligibilityStatus.addressId || '',
          userRegion: response.fuelEligibilityStatus.state || this.state.user.userRegion,
        });
        stateManager.setFilters({
          postcode: params.postcode,
          connectionScenario: params.connectionScenario,
          servicesRequired: {
            electricity: requiresElectricity,
            gas: requiresGas,
          },
        });

        const errorMessageObject = getPlanEligibilityError(
          response.fuelEligibilityStatus,
          params.servicesRequired,
          params.isBusiness,
          params.hasSolarPanels,
          response.plans,
        );

        if (errorMessageObject && errorMessageObject.trackingData) {
          if (errorMessageObject.action === 'NO_SOLAR') {
            stateManager.setFilters({
              ...this.state.filters,
              hasSolarPanels: false,
            });
          }
          errorForm({
            formName: 'compare-plans',
            errorMessage: errorMessageObject.trackingData,
          });
        }

        this.setState((state) => {
          return {
            alternateHierarchy: response.alternateHierarchy || false,
            user: {
              ...state.user,
              userAddressId: response.fuelEligibilityStatus.addressId || '',
              userRegion: response.fuelEligibilityStatus.state || this.state.user.userRegion,
            },
            addressUpdate: false,
            plans: response.plans || [],
            plansQueryEvent: {
              status: 'success',
            },
            fuelEligibilityStatus: {
              gas: response.fuelEligibilityStatus.gas || NONE,
              electricity: response.fuelEligibilityStatus.electricity || NONE,
            },
            filters: {
              ...state.filters,
              hasSolarPanels:
                safeGet(errorMessageObject, 'action') === 'NO_SOLAR'
                  ? false
                  : state.filters.hasSolarPanels,
              postcode: params.postcode,
              servicesRequired: {
                electricity: requiresElectricity,
                gas: requiresGas,
              },
            },
            error: errorMessageObject
              ? {
                  title: errorMessageObject.title,
                  message: errorMessageObject.message,
                  action: errorMessageObject.action,
                }
              : undefined,
          } as any;
        });

        this.triggerGetBpIdForPlans();
      })
      .catch((error) => {
        // If the error was 'cancelled' there is another request coming, so we want
        // to keep the loading icon

        if (error.message !== 'cancelled') {
          const errorMessageObject = getServerError(error, params.isBusiness);

          this.setState(partial(cpReducers.setAddressUpdate, false));
          this.setState(partial(cpReducers.handlePlansQueryFailure, error.message));
          this.setState(partial(cpReducers.setError, errorMessageObject));

          if (errorMessageObject && errorMessageObject.trackingData) {
            errorForm({
              formName: 'compare-plans',
              errorMessage: errorMessageObject.trackingData,
            });
          }
        }
      });

    if (!params.searchByPostcode) {
      stateManager.setUser({
        userDPID: safeGet(this.state, 'qasAddressObject.qasAddress.dpid', ''),
      });
    }
  };

  createPromisesArray = () => {
    let promiseArray = [];

    const { plans } = this.state;

    if (plans.length > 0) {
      promiseArray = plans.map((plan: IComparisonPlan) => {
        const param = {
          addressId: this.state.user.userAddressId || '',
          campaigns: [{ campaignId: plan.fuel[Object.keys(plan.fuel)[0]].campaignId }],
          customerType: this.state.user.isBusiness ? 'business' : ('residential' as any),
          url: this.props.factSheetApiEndPoint,
        };
        return getBPIDLinks(param);
      });
    }
    return { plans, promiseArray };
  };

  /**
   * Step 1 want the plansApiEndPoint so we set this value in contextHub
   * @param plan - selectedplan
   */
  selectPlan = (plan: IComparisonPlan) => {
    const plansApiEndPoint = this.props.plansApiEndPoint;
    stateManager.setSelectedPlan({ ...plan, plansApiEndPoint });
    const originSalesDetails = {
      [SELECTED_PLAN]: plan,
    };
    sessionStorage.setItem(ORIGIN_SALES_DETAIL, JSON.stringify(originSalesDetails));

    if (this.state.user.isResidential) {
      getWindow().location.assign(
        `${this.props.getStartedUrlResi}${getAddressIDQqueryParam(this.state)}`,
      );
    } else {
      getWindow().location.assign(
        `${this.props.getStartedUrlBusi}${getAddressIDQqueryParam(this.state)}`,
      );
    }
  };

  /**
   * Callback function for when a user changes their address.
   * @param qasAddressObject - object containing address properties such as unit, StreetNumber, streetName, City etc
   * @param id - Optional data-id of the input where address change triggered
   */
  updatePlans = (qasAddressObject: ConnectionAddressObject, id?: string) => {
    interaction('click', {
      appName: 'compare-plans',
      componentType: 'address',
      id: id || 'rates-address',
      label: id || 'rates-address',
    });

    this.setState(
      {
        qasAddressObject,
        user: {
          ...this.state.user,
          userRegion: qasAddressObject.qasAddress.state || this.state.user.userRegion,
          userAddressId: qasAddressObject.sapAddressId,
        },
        filters: {
          ...this.state.filters,
          postcode: safeGet(qasAddressObject, 'qasAddress.postcode', this.state.filters.postcode),
        },
        addressUpdate: true,
      },
      () => {
        const { servicesRequired } = this.state.filters;
        if (!servicesRequired.electricity && !servicesRequired.gas) {
          this.setState({
            filters: {
              ...this.state.filters,
              servicesRequired: { electricity: true, gas: true },
            },
          });
        }
        let params = Object.assign({}, this.state.filters, this.state.user);

        params = {
          ...params,
          addressId: qasAddressObject.sapAddressId,
          searchByPostcode: false,
        };

        this.getPlansApi(params);
      },
    );
  };

  handleFiltersChanged = (newFiltersValue) => {
    // Save the previous filter state for comparison later on
    const previousFilterState = this.state.filters;
    const newFilterState = cpReducers.updateFilterValues(newFiltersValue, this.state).filters;

    const refetchPlans = areFilterValuesBasicallyTheSame(newFilterState, previousFilterState);

    this.setState(
      {
        filters: newFilterState,
        addressUpdate: refetchPlans,
      },
      () => {
        stateManager.setFilters(this.state.filters);

        /**
         * When the filters are updated; if the update did not change the
         * renewable energy mix, request new plans.
         */
        if (refetchPlans) {
          // eslint-disable-next-line
          const searchByPostcode =
            this.state.filters.postcode !== previousFilterState.postcode ||
            this.state.user.userAddressId === '';

          this.getPlans(searchByPostcode);
        }
      },
    );
  };

  handleHelpMeChooseComplete = (answers) => {
    // Interprets answers from Help me Choose wizard and updates filters and plans on CP to reflect those answers
    const { filters, fuelEligibilityStatus, qasAddressObject, user } = this.state;
    const newFilters = {
      ...filters,
      postcode: answers.usePostcode ? answers.postcode : '',
      hasSolarPanels: answers.solar,
      renewableEnergy: {
        electricity: +(answers.greenElectricity || '0') * 100,
        gas: +(answers.greenGas || '0') * 100,
      },
      servicesRequired: {
        electricity: !!answers.electricity,
        gas: !!answers.gas,
      },
    };

    this.setState({
      user: {
        ...user,
        userRegion: answers.fuelEligibilityStatus.state || this.state.user.userRegion,
        userAddressId: safeGet(answers.address, 'sapAddressId') || '',
      },
      fuelEligibilityStatus: answers.fuelEligibilityStatus,
      filters: newFilters,
      qasAddressObject: answers.address,
      plans: answers.plans,
      recommendedPlan: answers.recommendedPlan,
    }, () => {
      stateManager.setFilters(this.state.filters);
    });
  };

  public plansSorted() {
    const { plans, user, filters, recommendedPlan } = this.state;
    const { personalisedPlanData } = this.props;

    let plansSorted = [];
    const nonExcludedPlans = excludePlans(plans, this.state.plansToExclude);

    const personalisedPlanOrder = safeGet(personalisedPlanData, 'order') || [];

    if (personalisedPlanOrder.length > 0) {
      plansSorted = personalisePlans(nonExcludedPlans, personalisedPlanData);
    } else if (nonExcludedPlans.length > 0) {
      plansSorted = sortPlans(
        nonExcludedPlans,
        user.userRegion,
        filters,
        this.getPlansOrder(),
      );

      stateManager.setTracking(getTrackingPlansNames(plansSorted));
    }

    if(user.userRegion === 'VIC' && user.isResidential) {
      plansSorted = doVicCulling(plansSorted);
    }
    else {
      plansSorted = doSolarShuffle(plansSorted, MAX_PLANS_TO_SHOW);
    }

    if (recommendedPlan) {
      plansSorted = injectRecommendedPlan(recommendedPlan, plansSorted, plans, MAX_PLANS_TO_SHOW);
    }

    return plansSorted;
  }

  public render = () => {
    const plansSorted = this.plansSorted();

    const { user, filters, showTargetTestTncs, qasAddressObject, addressUpdate } = this.state;
    const toolTipContent = getMessage('MONTHLY_CHARGES_MODAL');

    return (
      <MediaQueryProvider queries={CONFIG}>
        <div className="cp-root">
          <div className={'style--rebrand section section__header'}>
            <PersonalisedHeader
              postcode={this.state.filters.postcode}
              isBusiness={user.isBusiness}
            />
            <div className="section-row section-row__title">
              <h1>{STATIC_COPY.HEADER.PLANS_TITLE}</h1>
              <div className="description__container">
                <p className="description__paragraph" />
              </div>
            </div>
            <div className="section-row section-row__filters">
              <SettingsFilters
                hasSolarPanels={filters.hasSolarPanels}
                requiresElectricity={filters.servicesRequired.electricity}
                requiresGas={filters.servicesRequired.gas}
                renewableEnergy={filters.renewableEnergy}
                isCustomerMoving={filters.isCustomerMoving}
                fuelEligibilityStatus={this.state.fuelEligibilityStatus}
                postcode={filters.postcode}
                onChange={this.handleFiltersChanged}
                lpgCrossSellLink={getLPGCrossSellLink(user.isBusiness)}
                isBusiness={user.isBusiness}
                userRegion={user.userRegion}
                plans={plansSorted}
                address={qasAddressObject}
                updatePlans={this.updatePlans}
                addressApiProps={this.props.addressApiProps}
              />
            </div>
          </div>
          {this.state.error && (
            <div className="section section__body--error">
              <AlertBox type="error" heading={this.state.error.title}>
                <span
                  dangerouslySetInnerHTML={markUpUtils.createDangerousMarkupObject(
                    this.state.error.message,
                  )}
                />
              </AlertBox>
            </div>
          )}
          {this.state.plansQueryEvent.status === 'success' && plansSorted.length > 0 ? (
            <div className="section section__body">
              {!!user.isResidential &&
                <HelpMeChoose
                  addressApiProps={this.props.addressApiProps}
                  plansApiEndPoint={this.props.plansApiEndPoint}
                  addressReverseLookupApiEndPoint={this.props.addressApiProps.reverseLookupUrl}
                  answers={{
                    address: this.state.qasAddressObject,
                    plans: plansSorted,
                    postcode: this.state.filters.postcode,
                    fuelEligibilityStatus: this.state.fuelEligibilityStatus,
                  }}
                  onSelectPlan={this.selectPlan}
                  onComplete={this.handleHelpMeChooseComplete}
                  showModal={window.location.pathname.indexOf('help-me-choose') > -1}
                />
              }
              <PlansCarousel
                columnCount={plansSorted.length}
                columnCssSelector=".plan-comparison-card"
              >
                <PlansContainer
                  region={user.userRegion}
                  plans={plansSorted}
                  address={qasAddressObject}
                  planSelectCallback={this.selectPlan}
                  renewableEnergy={filters.renewableEnergy}
                  customerType={user.isResidential === true ? 'residential' : 'business'}
                  toolTipContent={toolTipContent}
                  postcode={filters.postcode}
                  isBusiness={user.isBusiness}
                />
              </PlansCarousel>
            </div>
          ) : null}
          {plansSorted.length > 0 && user.isResidential && (
            <div className="section section__body">
              <FAQ />
            </div>
          )}
          {plansSorted.length > 0 && (
            <div className="section section__footer">
              <TermsAndConditions
                terms={this.state.termsAndConditions}
                region={user.userRegion}
                showTargetTestTncs={showTargetTestTncs}
              />
            </div>
          )}
          {user.isBusiness && <CallUsBusiness />}
          <div id="mboxSalesRedirect" className="hidden" />
          {(this.state.plansQueryEvent.status === 'executing' || this.state.addressUpdate) && (
            <div>
              <div className="overlay" />
              <div className="overlay-spinner">
                <Spinner show={true} size={'lg'} />
              </div>
            </div>
          )}
        </div>
      </MediaQueryProvider>
    );
  };

  private triggerGetBpIdForPlans = () => {
    // createPromisesArray will return plansSorted and promiseArray in the same order
    const { plans, promiseArray } = this.createPromisesArray();
    if (promiseArray.length > 0) {
      return Promise.all(promiseArray).then((response: []) => {
        response.map((bpidLinksArray, index) => {
          plans[index].links = bpidLinksArray;
        });
        this.setState({ plans });
      });
    }
  };
}
